import { PANEL_TYPES, type WorkspacePanelState } from './types';

/** Versioned key so an incompatible saved layout can be invalidated safely. */
export const STORAGE_KEY = 'graphmind.workspace.layout.v1';

export interface PersistedLayout {
  version: 1;
  panels: WorkspacePanelState[];
  tabOrder: string[];
  activeId: string | null;
  maximizedId: string | null;
  zTop: number;
}

const VALID_MODES = new Set([
  'tabbed',
  'floating',
  'snapped-left',
  'snapped-right',
  'snapped-top-left',
  'snapped-top-right',
  'snapped-bottom-left',
  'snapped-bottom-right',
  'maximized',
]);

function validPanel(p: unknown): p is WorkspacePanelState {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.type === 'string' &&
    (PANEL_TYPES as readonly string[]).includes(o.type) &&
    typeof o.mode === 'string' &&
    VALID_MODES.has(o.mode as string)
  );
}

function isValid(data: unknown): data is PersistedLayout {
  if (!data || typeof data !== 'object') return false;
  const o = data as Record<string, unknown>;
  return o.version === 1 && Array.isArray(o.panels) && o.panels.every(validPanel) && Array.isArray(o.tabOrder);
}

export function saveLayout(data: PersistedLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/** The pre-rearchitecture tab list's key. The workspace layout supersedes it
 * (`STORAGE_KEY` above); this lingering blob is never read anymore, so drop it
 * on load so it can't rot in users' localStorage indefinitely. */
const LEGACY_TABS_KEY = 'graphmind.tabs';

/** Returns a valid layout, or null if missing/corrupt (caller loads default). */
export function loadLayout(): PersistedLayout | null {
  // Best-effort cleanup of the orphaned legacy key (separate try/catch so a
  // storage error here can never stop the real layout from loading).
  try {
    localStorage.removeItem(LEGACY_TABS_KEY);
  } catch {
    /* storage unavailable — non-fatal */
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
